import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Bell, Mail, CheckCircle, Clock, AlertCircle, Send, ToggleLeft, ToggleRight, Zap, Trash2, Users } from 'lucide-react';

const TEMPLATES = [
  {
    id: 'new_request', name: 'Nouvelle demande reçue', trigger: 'Formulaire B2C soumis',
    recipients: 'Admin + Équipe', channel: 'email', active: true,
    subject: '[M-CareLink] Nouvelle demande de séjour — {prenom} {nom}',
    lastSent: '2026-01-28', sentCount: 34
  },
  {
    id: 'status_update', name: 'Statut mis à jour', trigger: 'Changement de statut dossier',
    recipients: 'Aidant concerné', channel: 'email', active: true,
    subject: '[M-CareLink] Votre dossier a été mis à jour',
    lastSent: '2026-01-27', sentCount: 89
  },
  {
    id: 'booking_confirm', name: 'Réservation confirmée', trigger: 'Statut booking → confirmé',
    recipients: 'Aidant + Partenaire', channel: 'email', active: true,
    subject: '[M-CareLink] Votre séjour est confirmé du {start} au {end}',
    lastSent: '2026-01-20', sentCount: 12
  },
  {
    id: 'partner_welcome', name: 'Bienvenue Partenaire', trigger: 'Partenaire activé',
    recipients: 'Partenaire', channel: 'email', active: false,
    subject: '[M-CareLink] Bienvenue dans le réseau — votre espace partenaire',
    lastSent: null, sentCount: 7
  },
  {
    id: 'reminder_7d', name: 'Rappel — 7 jours avant séjour', trigger: '7 jours avant start_date',
    recipients: 'Aidant + Partenaire', channel: 'email', active: true,
    subject: '[M-CareLink] Rappel : votre séjour commence dans 7 jours',
    lastSent: '2026-01-15', sentCount: 8
  },
  {
    id: 'post_stay', name: 'Enquête post-séjour', trigger: '2 jours après end_date',
    recipients: 'Aidant', channel: 'email', active: false,
    subject: '[M-CareLink] Partagez votre expérience — 2 minutes',
    lastSent: null, sentCount: 15
  },
];

const RECENT_LOGS = [
  { template: 'Nouvelle demande reçue', to: 'admin@m-carelink.com', status: 'sent', date: '2026-01-28 14:32' },
  { template: 'Statut mis à jour', to: 'sophie.martin@email.com', status: 'sent', date: '2026-01-27 09:14' },
  { template: 'Réservation confirmée', to: 'contact@hotel-accessible.fr', status: 'sent', date: '2026-01-20 16:50' },
  { template: 'Rappel — 7 jours avant séjour', to: 'jean-pierre@email.com', status: 'failed', date: '2026-01-15 08:00' },
];

export default function NotificationsModule() {
  const [templates, setTemplates] = useState(TEMPLATES);

  const totalActive = templates.filter(t => t.active).length;
  const totalSent = templates.reduce((a, t) => a + t.sentCount, 0);

  const toggle = (id) => {
    setTemplates(templates.map(t => t.id === id ? { ...t, active: !t.active } : t));
    const tpl = templates.find(t => t.id === id);
    toast.success(`Notification "${tpl.name}" ${tpl.active ? 'désactivée' : 'activée'}`);
  };

  const testSend = (tpl) => {
    toast.success(`Email de test envoyé pour : ${tpl.name}`);
  };

  const remove = (id) => {
    const tpl = templates.find(t => t.id === id);
    if (!window.confirm(`Supprimer le template « ${tpl.name} » ?`)) return;
    setTemplates(templates.filter(t => t.id !== id));
    toast.success('Template supprimé');
  };

  return (
    <div className="p-6 space-y-6" data-testid="notifications-module">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-primary">Notifications</h2>
        <p className="text-sm text-muted-foreground font-body">Configuration des emails automatiques et rappels</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Actives', value: totalActive, Icon: CheckCircle, color: '#22863A' },
          { label: 'Inactives', value: templates.length - totalActive, Icon: Clock, color: '#8B5CF6' },
          { label: 'Emails envoyés', value: totalSent, Icon: Mail, color: '#5A3B31' },
          { label: 'Templates', value: templates.length, Icon: Bell, color: '#F47B20' },
        ].map(({ label, value, Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 border border-border shadow-sm flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: color + '18' }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div>
              <p className="font-heading text-xl font-semibold text-primary">{value}</p>
              <p className="text-xs text-muted-foreground font-body">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Provider info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3">
        <Zap className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <div>
          <span className="text-sm font-body text-blue-800"><strong>Fournisseur email :</strong> Resend (configuration requise)</span>
          <p className="text-xs text-blue-600 mt-0.5 font-body">Les emails sont actuellement en mode simulation. Configurez votre clé API Resend pour activer les envois réels.</p>
        </div>
      </div>

      {/* Templates */}
      <div className="space-y-3">
        {templates.map(t => (
          <div key={t.id} className="bg-white rounded-2xl border border-border shadow-sm p-5 hover:border-primary/20 transition-all" data-testid={`notif-template-${t.id}`}>
            <div className="flex items-start gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${t.active ? 'bg-green-50' : 'bg-muted'}`}>
                <Mail className={`w-4 h-4 ${t.active ? 'text-green-600' : 'text-muted-foreground'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="font-heading font-semibold text-primary text-sm">{t.name}</h3>
                  <Badge className={t.active ? 'bg-green-100 text-green-700 text-xs' : 'bg-gray-100 text-gray-500 text-xs'}>
                    {t.active ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground font-body font-mono bg-muted/50 px-2 py-0.5 rounded inline-block mb-2 truncate max-w-full">{t.subject}</p>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground font-body">
                  <span>Déclencheur : <strong className="text-foreground/80">{t.trigger}</strong></span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /><strong className="text-foreground/80">{t.recipients}</strong></span>
                  {t.lastSent && <span>Dernier envoi : {t.lastSent}</span>}
                  <span className="font-semibold text-primary">{t.sentCount} envoi(s)</span>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => testSend(t)} className="p-2 hover:bg-muted rounded-xl transition-colors" title="Envoyer un test" data-testid={`notif-test-${t.id}`}>
                  <Send className="w-4 h-4 text-muted-foreground" />
                </button>
                <button onClick={() => toggle(t.id)} className="p-1 hover:bg-muted rounded-xl transition-colors" data-testid={`notif-toggle-${t.id}`}>
                  {t.active
                    ? <ToggleRight className="w-6 h-6 text-green-600" />
                    : <ToggleLeft className="w-6 h-6 text-muted-foreground" />
                  }
                </button>
                <button onClick={() => remove(t.id)} className="p-2 hover:bg-red-50 rounded-xl transition-colors" title="Supprimer" data-testid={`notif-delete-${t.id}`}>
                  <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="text-center py-12 text-muted-foreground font-body text-sm">Aucun template de notification</div>
        )}
      </div>

      {/* Recent logs */}
      <div>
        <h3 className="font-heading font-semibold text-primary text-base mb-3">Journal récent</h3>
        <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
          {RECENT_LOGS.map((log, i) => (
            <div key={i} className={`flex items-center gap-4 px-5 py-3 ${i < RECENT_LOGS.length - 1 ? 'border-b border-border/50' : ''}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${log.status === 'sent' ? 'bg-green-100' : 'bg-red-100'}`}>
                {log.status === 'sent'
                  ? <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  : <AlertCircle className="w-3.5 h-3.5 text-red-600" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-body text-primary truncate block">{log.template}</span>
                <span className="text-xs text-muted-foreground font-body">{log.to}</span>
              </div>
              <span className="text-xs text-muted-foreground font-body flex-shrink-0">{log.date}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
